import React, { useEffect, useState } from 'react';
import Text from '../../../../core/utils/Text';
import Step1 from './Step1';
import Step2 from './Step2';
import Step3 from './Step3';
import Step4 from './Step4';
import { AnimatePresence, motion } from 'framer-motion';
import Step5 from './Step5';
import { PulseLoader } from 'react-spinners';
import Step6 from './Step6';
import FinalStep from './FinalStep';

const steps = [
    { title: "market-structure", step: Step1, },
    { title: "waykoff-phase", step: Step2, },
    { title: "smart-money", step: Step3, },
    { title: "liquidity-zone", step: Step4, },
    { title: "order-book-analysis", step: Step5, },
    { title: "final-result", step: Step6, },
    { title: "finalizing", step: FinalStep }
];

const stepDuration = 16000;
const statusDuration = 14000;

const AnalyzerLoading = ({ loading }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [done, setDone] = useState(false)

    useEffect(() => {
        if (!loading) return;
        setDone(false);

        const statusTimer = setTimeout(() => {
            setDone(true);
        }, statusDuration);

        return () => clearTimeout(statusTimer);
    }, [currentStep, loading]);

    useEffect(() => {
        if (!loading) return;

        let stepIndex = 0;
        setCurrentStep(stepIndex);

        const stepTimer = setInterval(() => {
            stepIndex++;
            if (stepIndex < steps.length - 1) {
                setCurrentStep(stepIndex);
            } else {
                clearInterval(stepTimer);
                setCurrentStep(steps.length - 1); // stay on "finalizing"
            }
        }, stepDuration);

        return () => clearInterval(stepTimer);
    }, [loading]);

    const StepComponent = steps[currentStep].step;

    return (
        <AnimatePresence mode="wait">
            <div className="relative w-full h-[584px] overflow-hidden px-4 md:px-0">
                {/* Background */}
                <motion.div
                    key={currentStep}
                    initial={{ y: 400, x: currentStep % 2 === 0 ? "-200" : "200", opacity: 0 }}
                    animate={{ y: 0, x: 0, opacity: 1 }}
                    exit={{ y: 400, x: 100, opacity: 0 }}
                    transition={{ ease: [0, 0.71, 0.2, 1.01], duration: 0.8 }}
                    className={`absolute right-0 bottom-0 z-[1]`}
                >
                    <img src={require('../../assets/loading/background.webp')} width={629} height={584} alt='' className={`${currentStep % 2 === 0 ? "scale-x-[-1]" : ""}`} />
                </motion.div>

                {/* Main Content */}
                <div className='flex h-full flex-col gap-4 items-center justify-center relative z-[2] '>

                    {/* Counter */}
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={currentStep}
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0, opacity: 0 }}
                            transition={{ type: "spring", stiffness: 500, damping: 20 }}
                            className='absolute top-0 ltr:left-3 rtl:right-3'
                        >
                            <span className="text-[100px] leading-none text-transparent bg-clip-text bg-gradient-to-b from-[#879AF1] to-[#E59089]">
                                {currentStep + 1}
                            </span>
                        </motion.div>
                    </AnimatePresence>

                    {/* Main Title */}
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={currentStep}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className='relative mb-8'>
                            <Text tid={steps[currentStep].title} className='text-4xl' />
                        </motion.div>
                    </AnimatePresence>
                    {/* Pulse Loader */}
                    <div className='absolute ltr:right-3 rtl:left-3 top-3'>
                        <div className='flex items-center gap-1'>
                            <Text tid={"analyzing"} className='text-mainColor' />
                            <PulseLoader color='#9f64df' size={6} speedMultiplier={0.5} />
                        </div>
                    </div>


                    {/* Step Component */}
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={currentStep}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ ease: [0, 0.71, 0.2, 1.01], duration: 0.9 }}
                            className="relative w-full h-full max-h-[428px] max-w-[559px] overflow-hidden bg-cardBgDark/95 rounded-xl p-4"
                        >

                            {StepComponent && <StepComponent done={done} />}
                        </motion.div>
                    </AnimatePresence>
                </div>

            </div>
        </AnimatePresence>
    );
};

export default AnalyzerLoading;
